// src/pages/CareersPage.tsx

import React, { FC } from 'react';
import styles from '../css/CareersPage.module.css';
import { motion, Variants } from 'framer-motion';
import { 
    Globe, 
    Cpu, 
    Code2, 
    ArrowRight, 
    Terminal,
    GitPullRequest,
    ShieldCheck,
    Workflow
} from 'lucide-react';

// Shared Components
import AnimatedSection from '../components/Shared/AnimatedSection';

// --- Data Structures ---
interface CulturePoint {
    icon: React.ReactNode;
    title: string;
    description: string;
}

const CareersPage: FC = () => {
    
    // Added 2 new cards for a total of 6
    const culturePoints: CulturePoint[] = [
        { 
            icon: <Globe size={24} strokeWidth={1.5} />, 
            title: "Remote-First & Async", 
            description: "We are a globally distributed team. We value deep work and written communication over endless meetings." 
        },
        { 
            icon: <Terminal size={24} strokeWidth={1.5} />, 
            title: "High-Impact Engineering", 
            description: "You will architect foundational systems for core business logic, not just maintain minor feature updates." 
        },
        { 
            icon: <Code2 size={24} strokeWidth={1.5} />, 
            title: "Industry-Embedded", 
            description: "We build solutions for problems we live through daily. Our engineers work alongside finance experts." 
        },
        { 
            icon: <GitPullRequest size={24} strokeWidth={1.5} />, 
            title: "Meritocracy & Ownership", 
            description: "We value logical rigor and output. You will have full ownership of complex technical challenges." 
        },
        { 
            icon: <ShieldCheck size={24} strokeWidth={1.5} />, 
            title: "Security-First Mindset", 
            description: "In FinTech, trust is currency. We integrate compliance and security scanning directly into our pipelines." 
        },
        { 
            icon: <Workflow size={24} strokeWidth={1.5} />, 
            title: "Automated Operations", 
            description: "We hate manual toil. From CI/CD to infrastructure provisioning, if it can be scripted, it is automated." 
        },
    ];
    
    // Added DevOps/Infrastructure tools
    const techStack = [
        "React & React Native", "TypeScript", "Python (Backend/ML)", 
        "Terraform", "AWS Lambda", "PostgreSQL", 
        "Docker", "Kubernetes", "GitHub Actions", 
        "Redis", "Datadog", "Next.js"
    ];

    // Animation Variants
    const containerVariants: Variants = { 
        visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } } 
    };
    
    const itemVariants: Variants = { 
        hidden: { opacity: 0, y: 20 }, 
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } 
    };

    return (
        <div className={styles.careersPage}>

            {/* 1. HERO SECTION */}
            <section className={styles.heroSection}>
                <div className={styles.gridBackground}></div>
                <div className={styles.container}>
                    <div className={styles.heroContent}>
                        <motion.div 
                            className={styles.heroWrapper}
                            initial="hidden" animate="visible" variants={containerVariants}
                        >
                            <motion.span variants={itemVariants} className={styles.heroBadge}>
                                Talent Acquisition
                            </motion.span>
                            
                            <motion.h1 variants={itemVariants} className={styles.heroTitle}>
                                Join the Engineers of <br/>
                                <span className={styles.titleHighlight}>Tomorrow's Business.</span>
                            </motion.h1>
                            
                            <motion.p variants={itemVariants} className={styles.heroSubtitle}>
                                We are seeking specialized talent who can bridge the gap between deep domain expertise and cutting-edge software architecture.
                            </motion.p>
                        </motion.div>
                    </div>
                </div>
                <div className={styles.heroFade}></div>
            </section>

            {/* 2. CULTURE GRID */}
            <AnimatedSection className={styles.cultureSection}>
                <div className={styles.container}>
                    <div className={styles.sectionHeaderCenter}>
                        <span className={styles.sectionLabel}>Our Ethos</span>
                        <h2 className={styles.sectionTitle}>Engineering Culture</h2>
                    </div>
                    
                    <motion.div className={styles.cultureGrid} variants={containerVariants}>
                        {culturePoints.map((point, index) => (
                            <motion.div key={index} className={styles.cultureCard} variants={itemVariants}>
                                {/* Layout Update: Icon beside Title */}
                                <div className={styles.cardHeader}>
                                    <div className={styles.iconBox}>{point.icon}</div>
                                    <h3 className={styles.cardTitle}>{point.title}</h3>
                                </div>
                                {/* Description Below */}
                                <p className={styles.cardText}>{point.description}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </AnimatedSection>
            
            {/* 3. TECH STACK */}
            <AnimatedSection className={styles.stackSection}>
                <div className={styles.container}>
                    <div className={styles.sectionHeaderCenter}>
                        <span className={styles.sectionLabel}>Infrastructure</span>
                        <h2 className={styles.sectionTitle}>The Tech Stack</h2>
                    </div>
                    
                    <motion.div className={styles.stackWrapper} variants={containerVariants}>
                        {techStack.map((skill, index) => (
                            <motion.div key={index} className={styles.stackItem} variants={itemVariants}>
                                <Cpu size={14} className={styles.stackIcon} />
                                <span className={styles.stackText}>{skill}</span>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </AnimatedSection>
            
            {/* 4. CTA */}
            <AnimatedSection className={styles.applySection}>
                <div className={styles.container}>
                    <motion.div className={styles.ctaBox} variants={itemVariants}>
                        <div className={styles.ctaContent}>
                            {/* Icon removed as requested */}
                            <h2 className={styles.ctaTitle}>Ready to Build the Core?</h2>
                            <p className={styles.ctaText}>
                                We manage all recruitment via our dedicated AI portal to ensure an unbiased, merit-based selection process.
                            </p>
                        </div>
                        <div className={styles.ctaAction}>
                            <a 
                                href="https://www.anna-ai.com/careers" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className={styles.applyButton}
                            >
                                Apply via ANNA AI <ArrowRight size={18} />
                            </a>
                        </div>
                    </motion.div>
                </div>
            </AnimatedSection>

        </div>
    );
};

export default CareersPage;